import { A11yModule } from "@angular/cdk/a11y";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { CdkTableModule } from "@angular/cdk/table";
import { CommonModule, DatePipe } from "@angular/common";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatSliderModule } from "@angular/material/slider";
import { MatTooltipModule } from "@angular/material/tooltip";
import { RouterModule } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NgbAccordionItem } from "@ng-bootstrap/ng-bootstrap";
import { ColorCompactModule } from "ngx-color/compact";
import { BooleanToYesNoPipe } from "../../pipe/boolean-to-yes-no.pipe";
import { BrowserFromUserAgentPipe } from "../../pipe/browser-from-user-agent.pipe";
import { FileSizeConverterPipe } from "../../pipe/filesize-convertor.pipe";
import { SafeHtmlPipe } from "../../pipe/safe-html.pipe";
import { TimeAgoPipe } from "../../pipe/time-ago.pipe";
import { TimeConverterPipe } from "../../pipe/time-convertor.pipe";
import { QuestionEditDecisionModalComponent } from "../dashboard-survey-products/question-edit-decision-modal/question-edit-decision-modal.component";
import { GenerateReportComponent } from "../dashboard/generate-report/generate-report.component";
import { ViewSurveyComponent } from "../dashboard/view-survey/view-survey.component";
import { AddPaperBundleFormComponent } from "./add-paper-bundle-form/add-paper-bundle-form.component";
import { AdvancedSearchComponent } from "./advanced-search/advanced-search.component";
import { AnswerStatusComponent } from "./answer-status/answer-status.component";
import { AuditHistoryComponent } from "./audit-history/audit-history.component";
import { AutoSaveStatusComponent } from "./auto-save-status/auto-save-status.component";
import { BenchmarkCompareComponent } from "./benchmark-components/benchmark-compare/benchmark-compare.component";
import { BenchmarkFilterViewComponent } from "./benchmark-components/benchmark-filter/benchmark-filter-view.component";
import { BenchmarkStatusComponent } from "./benchmark-components/benchmark-status/benchmark-status.component";
import { BenchmarkNavComponent } from "./benchmark-nav/benchmark-nav.component";
import { ColleagueEditFormComponent } from "./colleague-edit-form/colleague-edit-form.component";
import { ColleagueListComponent } from "./colleague-list/colleague-list.component";
import { ColleagueResponseStatsComponent } from "./colleague-response-stats/colleague-response-stats.component";
import { ColourControlComponent } from "./colour-control/colour-control.component";
import { CompanyComponent } from "./company/company.component";
import { ContactComponent } from "./contact/contact.component";
import { DealComponent } from "./deal/deal.component";
import { FormCompletionBaseComponent } from "./form-completion/form-completion-base.component";
import { FormCompletionTemplateQuestionComponent } from "./form-completion/form-completion-template-question/form-completion-template-question.component";
import { FormCompletionTemplateSectionComponent } from "./form-completion/form-completion-template-section/form-completion-template-section.component";
import { FormCompletionTemplateComponent } from "./form-completion/form-completion-template/form-completion-template.component";
import { QuestionNPSComponent } from "./form-completion/question-NPS/question-NPS.component";
import { QuestionDateComponent } from "./form-completion/question-date/question-date.component";
import { QuestionListComponent } from "./form-completion/question-list/question-list.component";
import { QuestionNumericComponent } from "./form-completion/question-numeric/question-numeric.component";
import { QuestionRangeComponent } from "./form-completion/question-range/question-range.component";
import { QuestionRankComponent } from "./form-completion/question-rank/question-rank.component";
import { QuestionTextComponent } from "./form-completion/question-text/question-text.component";
import { FormTemplateBaseComponent } from "./form-template-base/form-template-base.component";
import { FormTemplateQuestionBaseComponent } from "./form-template-base/form-template-question-base.component";
import { FormTemplateSectionBaseComponent } from "./form-template-base/form-template-section-base.component";
import { FormTemplateValidationComponent } from "./form-template-validation/form-template-validation.component";
import { IdleTimeoutModalComponent } from "./idle-timeout-modal/idle-timeout-modal.component";
import { ImpersonateUserHeaderComponent } from "./impersonate-user-header/impersonate-user-header.component";
import { LoadingComponent } from "./loading/loading.component";
import { MergeResponsesModalComponent } from "./merge-responses-modal/merge-responses-modal.component";
import { ConfirmDialogComponent } from "./modals/confirm-dialog/confirm-dialog.component";
import { DialogComponent } from "./modals/dialog/dialog.component";
import { NavComponent } from "./nav/nav.component";
import { PaginationComponent } from "./pagination/pagination.component";
import { PrintDocumentsComponent } from "./print-documents/print-documents.component";
import { PublicFooterComponent } from "./public-footer/public-footer.component";
import { PublicHeaderComponent } from "./public-header/public-header.component";
import { ResolveAlertComponent } from "./resolve-alert/resolve-alert.component";
import { ResponseFormQuestionListComponent } from "./response-verification/response-form-question-list/response-form-question-list.component";
import { ResponseFormQuestionRankComponent } from "./response-verification/response-form-question-rank/response-form-question-rank.component";
import { ResponseFormTemplateQuestionComponent } from "./response-verification/response-form-template-question/response-form-template-question.component";
import { ResponseFormTemplateSectionComponent } from "./response-verification/response-form-template-section/response-form-template-section.component";
import { ResponseFormTemplateComponent } from "./response-verification/response-form-template/response-form-template.component";
import { ScanViewerComponent } from "./scan-viewer/scan-viewer.component";
import { ScheduledEmailsComponent } from "./scheduled-emails/scheduled-emails.component";
import { SchedulerRefreshComponent } from "./scheduler-refresh/scheduler-refresh.component";
import { SecureButtonComponent } from "./secure-button/secure-button.component";
import { SecureFooterComponent } from "./secure-footer/secure-footer.component";
import { SecureHeaderClientComponent } from "./secure-header-client/secure-header-client.component";
import { SecureHeaderComponent } from "./secure-header/secure-header.component";
import { NotificationBellComponent } from "./notification-bell/notification-bell.component";
import { SecurePageClientComponent } from "./secure-page-client/secure-page-client.component";
import { SecurePageWithNavComponent } from "./secure-page-with-nav/secure-page-with-nav.component";
import { SelectFormTemplateComponent } from "./select-form-template/select-form-template.component";
import { SurveyInstanceComponent } from "./survey-instance/survey-instance.component";
import { SurveyProductsNavComponent } from "./survey-products-nav/survey-products-nav.component";
import { SurveyStatusComponent } from "./survey-status/survey-status.component";
import { TableComponent } from "./table/table.component";
import { FileUploadComponent } from "./upload/file-upload.component";
import { VersionUpdateModalComponent } from "./version-update-modal/version-update-modal.component";
import { ViewPaperBundleHistoryComponent } from "./view-paper-bundle-history/view-paper-bundle-history.component";

@NgModule({
  declarations: [
    AddPaperBundleFormComponent,
    AdvancedSearchComponent,
    AutoSaveStatusComponent,
    BenchmarkCompareComponent,
    BenchmarkFilterViewComponent,
    BenchmarkStatusComponent,
    BooleanToYesNoPipe,
    FileSizeConverterPipe,
    SafeHtmlPipe,
    BrowserFromUserAgentPipe,
    TimeAgoPipe,
    TimeConverterPipe,
    AnswerStatusComponent,
    FileUploadComponent,
    AuditHistoryComponent,
    ColleagueEditFormComponent,
    ColleagueListComponent,
    ColleagueResponseStatsComponent,
    ColourControlComponent,
    ContactComponent,
    CompanyComponent,
    DealComponent,
    FormTemplateValidationComponent,
    GenerateReportComponent,
    ImpersonateUserHeaderComponent,
    LoadingComponent,
    NavComponent,
    PaginationComponent,
    PrintDocumentsComponent,
    PublicFooterComponent,
    PublicHeaderComponent,
    SchedulerRefreshComponent,
    FormCompletionTemplateComponent,
    FormCompletionTemplateSectionComponent,
    FormCompletionTemplateQuestionComponent,
    QuestionTextComponent,
    QuestionRangeComponent,
    QuestionListComponent,
    FormCompletionBaseComponent,
    ResolveAlertComponent,
    SecureButtonComponent,
    SecureFooterComponent,
    SecureHeaderComponent,
    SecureHeaderClientComponent,
    NotificationBellComponent,
    SecurePageClientComponent,
    SecurePageWithNavComponent,
    SelectFormTemplateComponent,
    ScheduledEmailsComponent,
    SurveyInstanceComponent,
    SurveyStatusComponent,
    TableComponent,
    ConfirmDialogComponent,
    DialogComponent,
    ViewPaperBundleHistoryComponent,
    ScanViewerComponent,
    ViewSurveyComponent,
    FormTemplateBaseComponent,
    FormTemplateQuestionBaseComponent,
    FormTemplateSectionBaseComponent,
    ResponseFormTemplateComponent,
    ResponseFormTemplateQuestionComponent,
    ResponseFormTemplateSectionComponent,
    ResponseFormQuestionListComponent,
    ResponseFormQuestionRankComponent,
    MergeResponsesModalComponent,
    IdleTimeoutModalComponent,
    QuestionRankComponent,
    SurveyProductsNavComponent,
    BenchmarkNavComponent,
    QuestionNumericComponent,
    QuestionDateComponent,
    QuestionNPSComponent,
    QuestionEditDecisionModalComponent,
    VersionUpdateModalComponent,
  ],
  imports: [
    CommonModule,
    ColorCompactModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    RouterModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatSelectModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatInputModule,
    MatTooltipModule,
    MatIconModule,
    MatSliderModule,
    DragDropModule,
    NgbAccordionItem,
    A11yModule,
    CdkTableModule,
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatSelectModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatInputModule,
    MatTooltipModule,
    MatIconModule,
    MatSliderModule,
    AddPaperBundleFormComponent,
    AdvancedSearchComponent,
    AnswerStatusComponent,
    AutoSaveStatusComponent,
    FileUploadComponent,
    AuditHistoryComponent,
    BenchmarkCompareComponent,
    BenchmarkFilterViewComponent,
    BenchmarkStatusComponent,
    BrowserFromUserAgentPipe,
    ColleagueEditFormComponent,
    ColleagueListComponent,
    ColleagueResponseStatsComponent,
    ColourControlComponent,
    CompanyComponent,
    ContactComponent,
    DealComponent,
    FormTemplateValidationComponent,
    GenerateReportComponent,
    ImpersonateUserHeaderComponent,
    LoadingComponent,
    NavComponent,
    PaginationComponent,
    PrintDocumentsComponent,
    PublicFooterComponent,
    PublicHeaderComponent,
    ResolveAlertComponent,
    SecureButtonComponent,
    SecureFooterComponent,
    SecureHeaderComponent,
    SecureHeaderClientComponent,
    NotificationBellComponent,
    SecurePageClientComponent,
    SecurePageWithNavComponent,
    SelectFormTemplateComponent,
    SchedulerRefreshComponent,
    FormCompletionTemplateComponent,
    ResponseFormTemplateComponent,
    ScheduledEmailsComponent,
    SurveyInstanceComponent,
    SurveyStatusComponent,
    TableComponent,
    ConfirmDialogComponent,
    DialogComponent,
    VersionUpdateModalComponent,
    ViewPaperBundleHistoryComponent,
    ScanViewerComponent,
    ViewSurveyComponent,
    TimeAgoPipe,
    TimeConverterPipe,
    SurveyProductsNavComponent,
    BenchmarkNavComponent,
    QuestionEditDecisionModalComponent,
    SafeHtmlPipe,
  ],
  providers: [DatePipe],
})
export class SharedModule {}
